import { api } from '@/lib/apiClient'
import { ensureTemplatePresetConsistency } from '@/lib/badgeTemplate'
import type { BadgePrintData, BadgePrintLog, BadgePrintRegistrant, BadgeTemplate } from '@/types/badgeDesigner'

type TriggerPrintResponse = {
  success: boolean
  badgePrintLog?: BadgePrintLog
  printLogs?: BadgePrintLog[]
  printedCount?: number
  queuedCount?: number
  scanAttendanceCreated?: boolean
  printJob?: {
    id?: number | string
    status?: string
  }
  job?: {
    id?: number | string
    status?: string
  }
}

export type TriggerRegistrantBadgePrintParams = {
  eventSlug: string
  registrantId: number
  printerUid: string
  printerId?: string | null
  printerName?: string
  stationId?: string | null
  printStationPrinterId?: string | null
  printStationId?: string | null
  printerProfileId?: string | null
  template: BadgeTemplate
  data: BadgePrintData
  source?: string
}

export type TriggerRegistrantBadgePrintResult = TriggerPrintResponse & {
  sidesCount: number
}

export const toPrintRegistrantPayload = (
  registrant: BadgePrintRegistrant & { id: number }
): BadgePrintRegistrant => ({
  id: registrant.id,
  full_name: registrant.full_name,
  email: registrant.email,
  phone: registrant.phone,
  qr_value: registrant.qr_value,
  answers: registrant.answers || {},
})

export async function triggerRegistrantBadgePrint({
  eventSlug,
  registrantId,
  printerUid,
  printerId,
  printerName,
  stationId,
  printStationPrinterId,
  printStationId,
  printerProfileId,
  template,
  data,
  source = 'attendee_management',
}: TriggerRegistrantBadgePrintParams): Promise<TriggerRegistrantBadgePrintResult> {
  const normalizedTemplate = ensureTemplatePresetConsistency(template)
  const sidesCount = normalizedTemplate.layout_mode === 'double_sided' ? 2 : 1
  const resolvedPrintStationPrinterId = printStationPrinterId || printerId
  const resolvedPrintStationId = printStationId || stationId

  const response = await api.post<TriggerPrintResponse>(
    `/events/${eventSlug}/badge_printings/trigger_print`,
    {
      registrant_id: registrantId,
      source,
      status: 'pending',
      printer_name: printerName,
      printer_uid: printerUid,
      printer_id: printerId,
      print_station_printer_id: resolvedPrintStationPrinterId,
      print_station_id: resolvedPrintStationId,
      printer_profile_id: printerProfileId,
      sides_count: sidesCount,
      requested_at: new Date().toISOString(),
      meta: {
        print_flow: 'agent',
        requested_from: 'ava_frontend',
        registrant: {
          id: data.registrant.id,
          qr_value: data.registrant.qr_value,
        },
        event: {
          id: data.event?.id,
          slug: data.event?.slug,
        },
      },
    }
  )

  return {
    ...response,
    sidesCount,
  }
}
